<template>
  <div>
    <FloatingMenu v-if="editor" :editor="editor" plugin-key="default">
      <!-- If editing styles, copy them to the duplicated instances in for loops (@todo componentise buttons to fix that) -->
      <div
        class="editor-menu items-center px-2 py-1 bg-white border border-truegray-300 shadow"
      >
        <div class="flex items-center">
          <!-- Headings dropdown -->
          <button
            v-if="isFeatureActive('headings')"
            type="button"
            class="editor-menu__button has-children"
            :class="{
              'is-active':
                activeDropdown === 'headings' || editor.isActive('heading'),
            }"
            @click="toggleDropdown('headings')"
          >
            <EditorIcon name="headings" title="Headings" :has-children="true" />
            <div
              v-if="activeDropdown === 'headings'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('h1')"
                type="button"
                class="editor-menu__button justify-center items-center"
                :class="{
                  'is-active': editor.isActive('heading', { level: 1 }),
                }"
                @click="
                  editor.chain().focus().toggleHeading({ level: 1 }).run()
                "
              >
                <EditorIcon name="h1" title="Heading 1" />
              </button>

              <button
                v-if="isFeatureActive('h2')"
                type="button"
                class="editor-menu__button justify-center items-center font-primary"
                :class="{
                  'is-active': editor.isActive('heading', { level: 2 }),
                }"
                @click="
                  editor.chain().focus().toggleHeading({ level: 2 }).run()
                "
              >
                <EditorIcon name="h2" title="Heading 2" />
              </button>

              <button
                v-if="isFeatureActive('h3')"
                type="button"
                class="editor-menu__button justify-center items-center font-primary"
                :class="{
                  'is-active': editor.isActive('heading', { level: 3 }),
                }"
                @click="
                  editor.chain().focus().toggleHeading({ level: 3 }).run()
                "
              >
                <EditorIcon name="h3" title="Heading 3" />
              </button>
            </div>
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('bold')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('bold') }"
            @click="editor.chain().focus().toggleBold().run()"
          >
            <EditorIcon name="bold" title="Bold" />
          </button>
          <button
            v-if="isFeatureActive('italic')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('italic') }"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <EditorIcon name="italic" title="Italic" />
          </button>

          <button
            v-if="isFeatureActive('underline')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('underline') }"
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <EditorIcon name="underline" title="Underline" />
          </button>

          <button
            v-if="isFeatureActive('strike')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('strike') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <EditorIcon name="strike" title="Strikethrough" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('text_colour')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('text_colour') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <EditorIcon name="text_colour" title="Text colour" />
          </button>

          <button
            v-if="isFeatureActive('highlight')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('highlight') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <EditorIcon name="highlight" title="Highlight" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <!-- Lists dropdown -->
          <button
            v-if="isFeatureActive('lists')"
            type="button"
            class="editor-menu__button has-children"
            :class="{
              'is-active':
                activeDropdown === 'lists' ||
                editor.isActive('bulletList') ||
                editor.isActive('orderedList'),
            }"
            @click="toggleDropdown('lists')"
          >
            <EditorIcon name="ol" title="Lists" :has-children="true" />
            <div
              v-if="activeDropdown === 'lists'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('bulletList')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': editor.isActive('bulletList') }"
                @click="editor.chain().focus().toggleBulletList().run()"
              >
                <EditorIcon name="ul" title="Bullet list" />
              </button>

              <button
                v-if="isFeatureActive('orderedList')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': editor.isActive('orderedList') }"
                @click="editor.chain().focus().toggleOrderedList().run()"
              >
                <EditorIcon name="ol" title="Numbered list" />
              </button>
            </div>
          </button>

          <!-- Alignment dropdown -->
          <button
            v-if="isFeatureActive('alignments')"
            type="button"
            class="editor-menu__button has-children"
            :class="{
              'is-active':
                activeDropdown === 'alignments' ||
                editor.isActive({ textAlign: 'center' }) ||
                editor.isActive({ textAlign: 'right' }),
            }"
            @click="toggleDropdown('alignments')"
          >
            <EditorIcon
              name="align_right"
              title="Text alignment"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'alignments'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('alignLeft')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
                @click="handleTextAlign('left')"
              >
                <EditorIcon name="align_left" title="Align left" />
              </button>

              <button
                v-if="isFeatureActive('alignCenter')"
                type="button"
                class="editor-menu__button"
                :class="{
                  'is-active': editor.isActive({ textAlign: 'center' }),
                }"
                @click="handleTextAlign('center')"
              >
                <EditorIcon name="align_center" title="Align center" />
              </button>

              <button
                v-if="isFeatureActive('alignRight')"
                type="button"
                class="editor-menu__button"
                :class="{
                  'is-active': editor.isActive({ textAlign: 'right' }),
                }"
                @click="handleTextAlign('right')"
              >
                <EditorIcon name="align_right" title="Align right" />
              </button>
            </div>
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('blockquote')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('blockquote') }"
            @click="editor.chain().focus().toggleBlockquote().run()"
          >
            <EditorIcon name="quote" title="Block quote" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('undo')"
            type="button"
            class="editor-menu__button"
            :disabled="!editor.can().undo()"
            @click="editor.chain().focus().undo().run()"
          >
            <EditorIcon name="undo" title="Undo" />
          </button>

          <button
            v-if="isFeatureActive('redo')"
            type="button"
            class="editor-menu__button"
            :disabled="!editor.can().redo()"
            @click="editor.chain().focus().redo().run()"
          >
            <EditorIcon name="redo" title="Redo" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('code')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('code') }"
            @click="editor.chain().focus().toggleCode().run()"
          >
            <EditorIcon name="code" title="Code" />
          </button>

          <button
            v-if="isFeatureActive('codeBlock')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('codeBlock') }"
            @click="editor.chain().focus().toggleCodeBlock().run()"
          >
            <EditorIcon name="code_block" title="Code block" />
          </button>

          <button
            v-if="isFeatureActive('horizontalRule')"
            type="button"
            class="editor-menu__button"
            @click="editor.chain().focus().setHorizontalRule().run()"
          >
            <EditorIcon name="hr" title="Horizontal rule" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('image')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('ImageResizable') }"
            @click="openImageModal"
          >
            <EditorIcon name="image" title="Add image" />
          </button>

          <button
            v-if="isFeatureActive('link')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('link') }"
            @click="openLinkModal"
          >
            <EditorIcon name="link" title="Add/edit link" />
          </button>

          <button
            v-if="showUnsetLink"
            type="button"
            class="editor-menu__button"
            :disabled="
              !editor.can().unsetLink() &&
              !editor.getAttributes('ImageResizable').link
            "
            @click="handleUnsetLink"
          >
            <EditorIcon name="remove_link" title="Remove link" />
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children"
            @click="toggleDropdown('table')"
          >
            <EditorIcon
              name="add_table"
              title="Add/remove table"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                @click="
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                    .run()
                "
              >
                <EditorIcon name="add_table" title="Add table" />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().deleteTable()"
                @click="editor.chain().focus().deleteTable().run()"
              >
                <EditorIcon name="delete_table" title="Delete table" />
              </button>
            </div>
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children"
            @click="toggleDropdown('table-row')"
          >
            <EditorIcon
              name="add_row_after"
              title="Add/remove row"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table-row'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().addRowAfter()"
                @click="editor.chain().focus().addRowAfter().run()"
              >
                <EditorIcon name="add_row_after" title="Add table row after" />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().deleteRow()"
                @click="editor.chain().focus().deleteRow().run()"
              >
                <EditorIcon name="delete_row" title="Delete table row after" />
              </button>
            </div>
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children"
            @click="toggleDropdown('table-col')"
          >
            <EditorIcon
              name="add_col_after"
              title="Add/remove column"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table-col'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().addColumnAfter()"
                @click="editor.chain().focus().addColumnAfter().run()"
              >
                <EditorIcon
                  name="add_col_after"
                  title="Add table column after"
                />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().deleteColumn()"
                @click="editor.chain().focus().deleteColumn().run()"
              >
                <EditorIcon
                  name="delete_col"
                  title="Delete table column after"
                />
              </button>
            </div>
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children is-highlighted"
            @click="toggleDropdown('table-format')"
          >
            <EditorIcon
              name="remove_border"
              title="Format table"
              :is-suggested="true"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table-format'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                @click="() => unsetTable(false)"
              >
                <EditorIcon
                  name="add_border"
                  title="Show table borders"
                  :is-suggested="true"
                />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                @click="() => unsetTable(true)"
              >
                <EditorIcon
                  name="remove_border"
                  title="Hide table borders"
                  :is-suggested="true"
                />
              </button>
            </div>
          </button>

          <!-- Lists dropdown -->
          <button
            v-if="isFeatureActive('wand')"
            type="button"
            class="editor-menu__button has-children is-highlighted rounded-sm"
            :class="{
              'is-active':
                activeDropdown === 'wand' ||
                pasteOption === 'paste_without_formatting' ||
                pasteOption === 'paste_from_email',
            }"
            @click="toggleDropdown('wand')"
          >
            <EditorIcon
              name="wand"
              title="Special formatting"
              :is-suggested="true"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'wand'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('paste_without_formatting')"
                type="button"
                class="editor-menu__button"
                :class="{
                  'is-active': pasteOption === 'paste_without_formatting',
                }"
                @click="setPasteOption('paste_without_formatting')"
              >
                <EditorIcon
                  name="paste_without_formatting"
                  title="Paste without formatting"
                  :is-suggested="true"
                />
              </button>

              <button
                v-if="isFeatureActive('paste_from_email')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': pasteOption === 'paste_from_email' }"
                @click="setPasteOption('paste_from_email')"
              >
                <EditorIcon
                  name="paste_from_email"
                  title="Paste from email"
                  :is-suggested="true"
                />
              </button>

              <button
                type="button"
                class="editor-menu__button flex items-center justify-center text-truegray-300 hover:text-black"
                title="What's this?"
                @click="showWandInfo"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  class="w-4 h-4"
                >
                  <path
                    fill-rule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zM8.94 6.94a.75.75 0 11-1.061-1.061 3 3 0 112.871 5.026v.345a.75.75 0 01-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 108.94 6.94zM10 15a1 1 0 100-2 1 1 0 000 2z"
                    clip-rule="evenodd"
                  />
                </svg>
              </button>
            </div>
          </button>
        </div>

        <!--
          <button
          type="button"
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="toggleDropdown('wand')"
          >
          <EditorIcon name="close" title="Close" />
          </button> 
        -->
      </div>
    </FloatingMenu>
    <div v-for="key in getMenuItems('FloatingMenu')" :key="key">
      <FloatingMenu
        v-if="editor"
        :editor="editor"
        :plugin-key="key"
        :should-show="(attr) => shouldShowMenu(attr, 'FloatingMenu', key)"
      >
        <div
          class="editor-menu items-center px-2 py-1 bg-white border border-truegray-300 shadow"
        >
          <div class="flex items-center">
            <!-- Headings dropdown -->
            <button
              v-if="isFeatureActive('headings')"
              type="button"
              class="editor-menu__button has-children"
              :class="{
                'is-active':
                  activeDropdown === 'headings' || editor.isActive('heading'),
              }"
              @click="toggleDropdown('headings')"
            >
              <EditorIcon
                name="headings"
                title="Headings"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'headings'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('h1')"
                  type="button"
                  class="editor-menu__button justify-center items-center"
                  :class="{
                    'is-active': editor.isActive('heading', { level: 1 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  "
                >
                  <EditorIcon name="h1" title="Heading 1" />
                </button>

                <button
                  v-if="isFeatureActive('h2')"
                  type="button"
                  class="editor-menu__button justify-center items-center font-primary"
                  :class="{
                    'is-active': editor.isActive('heading', { level: 2 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  "
                >
                  <EditorIcon name="h2" title="Heading 2" />
                </button>

                <button
                  v-if="isFeatureActive('h3')"
                  type="button"
                  class="editor-menu__button justify-center items-center font-primary"
                  :class="{
                    'is-active': editor.isActive('heading', { level: 3 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  "
                >
                  <EditorIcon name="h3" title="Heading 3" />
                </button>
              </div>
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('bold')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('bold') }"
              @click="editor.chain().focus().toggleBold().run()"
            >
              <EditorIcon name="bold" title="Bold" />
            </button>
            <button
              v-if="isFeatureActive('italic')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('italic') }"
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <EditorIcon name="italic" title="Italic" />
            </button>

            <button
              v-if="isFeatureActive('underline')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('underline') }"
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <EditorIcon name="underline" title="Underline" />
            </button>

            <button
              v-if="isFeatureActive('strike')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('strike') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <EditorIcon name="strike" title="Strikethrough" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('text_colour')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <EditorIcon name="text_colour" title="Text colour" />
            </button>

            <button
              v-if="isFeatureActive('highlight')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('highlight') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <EditorIcon name="highlight" title="Highlight" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <!-- Lists dropdown -->
            <button
              v-if="isFeatureActive('lists')"
              type="button"
              class="editor-menu__button has-children"
              :class="{
                'is-active':
                  activeDropdown === 'lists' ||
                  editor.isActive('bulletList') ||
                  editor.isActive('orderedList'),
              }"
              @click="toggleDropdown('lists')"
            >
              <EditorIcon name="ol" title="Lists" :has-children="true" />
              <div
                v-if="activeDropdown === 'lists'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('bulletList')"
                  type="button"
                  class="editor-menu__button"
                  :class="{ 'is-active': editor.isActive('bulletList') }"
                  @click="editor.chain().focus().toggleBulletList().run()"
                >
                  <EditorIcon name="ul" title="Bullet list" />
                </button>

                <button
                  v-if="isFeatureActive('orderedList')"
                  type="button"
                  class="editor-menu__button"
                  :class="{ 'is-active': editor.isActive('orderedList') }"
                  @click="editor.chain().focus().toggleOrderedList().run()"
                >
                  <EditorIcon name="ol" title="Numbered list" />
                </button>
              </div>
            </button>

            <!-- Alignment dropdown -->
            <button
              v-if="isFeatureActive('alignments')"
              type="button"
              class="editor-menu__button has-children"
              :class="{
                'is-active':
                  activeDropdown === 'alignments' ||
                  editor.isActive({ textAlign: 'center' }) ||
                  editor.isActive({ textAlign: 'right' }),
              }"
              @click="toggleDropdown('alignments')"
            >
              <EditorIcon
                name="align_right"
                title="Text alignment"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'alignments'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('alignLeft')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': editor.isActive({ textAlign: 'left' }),
                  }"
                  @click="handleTextAlign('left')"
                >
                  <EditorIcon name="align_left" title="Align left" />
                </button>

                <button
                  v-if="isFeatureActive('alignCenter')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': editor.isActive({ textAlign: 'center' }),
                  }"
                  @click="handleTextAlign('center')"
                >
                  <EditorIcon name="align_center" title="Align center" />
                </button>

                <button
                  v-if="isFeatureActive('alignRight')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': editor.isActive({ textAlign: 'right' }),
                  }"
                  @click="handleTextAlign('right')"
                >
                  <EditorIcon name="align_right" title="Align right" />
                </button>
              </div>
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('blockquote')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('blockquote') }"
              @click="editor.chain().focus().toggleBlockquote().run()"
            >
              <EditorIcon name="quote" title="Block quote" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('undo')"
              type="button"
              class="editor-menu__button"
              :disabled="!editor.can().undo()"
              @click="editor.chain().focus().undo().run()"
            >
              <EditorIcon name="undo" title="Undo" />
            </button>

            <button
              v-if="isFeatureActive('redo')"
              type="button"
              class="editor-menu__button"
              :disabled="!editor.can().redo()"
              @click="editor.chain().focus().redo().run()"
            >
              <EditorIcon name="redo" title="Redo" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('code')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('code') }"
              @click="editor.chain().focus().toggleCode().run()"
            >
              <EditorIcon name="code" title="Code" />
            </button>

            <button
              v-if="isFeatureActive('codeBlock')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('codeBlock') }"
              @click="editor.chain().focus().toggleCodeBlock().run()"
            >
              <EditorIcon name="code_block" title="Code block" />
            </button>

            <button
              v-if="isFeatureActive('horizontalRule')"
              type="button"
              class="editor-menu__button"
              @click="editor.chain().focus().setHorizontalRule().run()"
            >
              <EditorIcon name="hr" title="Horizontal rule" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('image')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('ImageResizable') }"
              @click="openImageModal"
            >
              <EditorIcon name="image" title="Add image" />
            </button>

            <button
              v-if="isFeatureActive('link')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('link') }"
              @click="openLinkModal"
            >
              <EditorIcon name="link" title="Add/edit link" />
            </button>

            <button
              v-if="showUnsetLink"
              type="button"
              class="editor-menu__button"
              :disabled="
                !editor.can().unsetLink() &&
                !editor.getAttributes('ImageResizable').link
              "
              @click="handleUnsetLink"
            >
              <EditorIcon name="remove_link" title="Remove link" />
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children"
              @click="toggleDropdown('table')"
            >
              <EditorIcon
                name="add_table"
                title="Add/remove table"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  @click="
                    editor
                      .chain()
                      .focus()
                      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                      .run()
                  "
                >
                  <EditorIcon name="add_table" title="Add table" />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().deleteTable()"
                  @click="editor.chain().focus().deleteTable().run()"
                >
                  <EditorIcon name="delete_table" title="Delete table" />
                </button>
              </div>
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children"
              @click="toggleDropdown('table-row')"
            >
              <EditorIcon
                name="add_row_after"
                title="Add/remove row"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table-row'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().addRowAfter()"
                  @click="editor.chain().focus().addRowAfter().run()"
                >
                  <EditorIcon
                    name="add_row_after"
                    title="Add table row after"
                  />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().deleteRow()"
                  @click="editor.chain().focus().deleteRow().run()"
                >
                  <EditorIcon
                    name="delete_row"
                    title="Delete table row after"
                  />
                </button>
              </div>
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children"
              @click="toggleDropdown('table-col')"
            >
              <EditorIcon
                name="add_col_after"
                title="Add/remove column"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table-col'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().addColumnAfter()"
                  @click="editor.chain().focus().addColumnAfter().run()"
                >
                  <EditorIcon
                    name="add_col_after"
                    title="Add table column after"
                  />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().deleteColumn()"
                  @click="editor.chain().focus().deleteColumn().run()"
                >
                  <EditorIcon
                    name="delete_col"
                    title="Delete table column after"
                  />
                </button>
              </div>
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children is-highlighted"
              @click="toggleDropdown('table-format')"
            >
              <EditorIcon
                name="remove_border"
                title="Format table"
                :is-suggested="true"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table-format'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  @click="() => unsetTable(false)"
                >
                  <EditorIcon
                    name="add_border"
                    title="Show table borders"
                    :is-suggested="true"
                  />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  @click="() => unsetTable(true)"
                >
                  <EditorIcon
                    name="remove_border"
                    title="Hide table borders"
                    :is-suggested="true"
                  />
                </button>
              </div>
            </button>

            <!-- Lists dropdown -->
            <button
              v-if="isFeatureActive('wand')"
              type="button"
              class="editor-menu__button has-children is-highlighted rounded-sm"
              :class="{
                'is-active':
                  activeDropdown === 'wand' ||
                  pasteOption === 'paste_without_formatting' ||
                  pasteOption === 'paste_from_email',
              }"
              @click="toggleDropdown('wand')"
            >
              <EditorIcon
                name="wand"
                title="Special formatting"
                :is-suggested="true"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'wand'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('paste_without_formatting')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': pasteOption === 'paste_without_formatting',
                  }"
                  @click="setPasteOption('paste_without_formatting')"
                >
                  <EditorIcon
                    name="paste_without_formatting"
                    title="Paste without formatting"
                    :is-suggested="true"
                  />
                </button>

                <button
                  v-if="isFeatureActive('paste_from_email')"
                  type="button"
                  class="editor-menu__button"
                  :class="{ 'is-active': pasteOption === 'paste_from_email' }"
                  @click="setPasteOption('paste_from_email')"
                >
                  <EditorIcon
                    name="paste_from_email"
                    title="Paste from email"
                    :is-suggested="true"
                  />
                </button>
              </div>
            </button>
          </div>

          <!--
            <button
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('bulletList') }"
            @click="toggleDropdown('wand')"
            >
            <EditorIcon name="close" title="Close" />
            </button> 
          -->
        </div>
      </FloatingMenu>
    </div>

    <BubbleMenu v-if="editor" :editor="editor" plugin-key="default">
      <div
        class="editor-menu items-center px-2 py-1 bg-white border border-truegray-300 shadow"
      >
        <div class="flex items-center">
          <!-- Headings dropdown -->
          <button
            v-if="isFeatureActive('headings')"
            type="button"
            class="editor-menu__button has-children"
            :class="{
              'is-active':
                activeDropdown === 'headings' || editor.isActive('heading'),
            }"
            @click="toggleDropdown('headings')"
          >
            <EditorIcon name="headings" title="Headings" :has-children="true" />
            <div
              v-if="activeDropdown === 'headings'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('h1')"
                type="button"
                class="editor-menu__button justify-center items-center"
                :class="{
                  'is-active': editor.isActive('heading', { level: 1 }),
                }"
                @click="
                  editor.chain().focus().toggleHeading({ level: 1 }).run()
                "
              >
                <EditorIcon name="h1" title="Heading 1" />
              </button>

              <button
                v-if="isFeatureActive('h2')"
                type="button"
                class="editor-menu__button justify-center items-center font-primary"
                :class="{
                  'is-active': editor.isActive('heading', { level: 2 }),
                }"
                @click="
                  editor.chain().focus().toggleHeading({ level: 2 }).run()
                "
              >
                <EditorIcon name="h2" title="Heading 2" />
              </button>

              <button
                v-if="isFeatureActive('h3')"
                type="button"
                class="editor-menu__button justify-center items-center font-primary"
                :class="{
                  'is-active': editor.isActive('heading', { level: 3 }),
                }"
                @click="
                  editor.chain().focus().toggleHeading({ level: 3 }).run()
                "
              >
                <EditorIcon name="h3" title="Heading 3" />
              </button>
            </div>
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('bold')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('bold') }"
            @click="editor.chain().focus().toggleBold().run()"
          >
            <EditorIcon name="bold" title="Bold" />
          </button>
          <button
            v-if="isFeatureActive('italic')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('italic') }"
            @click="editor.chain().focus().toggleItalic().run()"
          >
            <EditorIcon name="italic" title="Italic" />
          </button>

          <button
            v-if="isFeatureActive('underline')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('underline') }"
            @click="editor.chain().focus().toggleUnderline().run()"
          >
            <EditorIcon name="underline" title="Underline" />
          </button>

          <button
            v-if="isFeatureActive('strike')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('strike') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <EditorIcon name="strike" title="Strikethrough" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('text_colour')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('text_colour') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <EditorIcon name="text_colour" title="Text colour" />
          </button>

          <button
            v-if="isFeatureActive('highlight')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('highlight') }"
            @click="editor.chain().focus().toggleStrike().run()"
          >
            <EditorIcon name="highlight" title="Highlight" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <!-- Lists dropdown -->
          <button
            v-if="isFeatureActive('lists')"
            type="button"
            class="editor-menu__button has-children"
            :class="{
              'is-active':
                activeDropdown === 'lists' ||
                editor.isActive('bulletList') ||
                editor.isActive('orderedList'),
            }"
            @click="toggleDropdown('lists')"
          >
            <EditorIcon name="ol" title="Lists" :has-children="true" />
            <div
              v-if="activeDropdown === 'lists'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('bulletList')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': editor.isActive('bulletList') }"
                @click="editor.chain().focus().toggleBulletList().run()"
              >
                <EditorIcon name="ul" title="Bullet list" />
              </button>

              <button
                v-if="isFeatureActive('orderedList')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': editor.isActive('orderedList') }"
                @click="editor.chain().focus().toggleOrderedList().run()"
              >
                <EditorIcon name="ol" title="Numbered list" />
              </button>
            </div>
          </button>

          <!-- Alignment dropdown -->
          <button
            v-if="isFeatureActive('alignments')"
            type="button"
            class="editor-menu__button has-children"
            :class="{
              'is-active':
                activeDropdown === 'alignments' ||
                editor.isActive({ textAlign: 'center' }) ||
                editor.isActive({ textAlign: 'right' }),
            }"
            @click="toggleDropdown('alignments')"
          >
            <EditorIcon
              name="align_right"
              title="Text alignment"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'alignments'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('alignLeft')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': editor.isActive({ textAlign: 'left' }) }"
                @click="handleTextAlign('left')"
              >
                <EditorIcon name="align_left" title="Align left" />
              </button>

              <button
                v-if="isFeatureActive('alignCenter')"
                type="button"
                class="editor-menu__button"
                :class="{
                  'is-active': editor.isActive({ textAlign: 'center' }),
                }"
                @click="handleTextAlign('center')"
              >
                <EditorIcon name="align_center" title="Align center" />
              </button>

              <button
                v-if="isFeatureActive('alignRight')"
                type="button"
                class="editor-menu__button"
                :class="{
                  'is-active': editor.isActive({ textAlign: 'right' }),
                }"
                @click="handleTextAlign('right')"
              >
                <EditorIcon name="align_right" title="Align right" />
              </button>
            </div>
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('blockquote')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('blockquote') }"
            @click="editor.chain().focus().toggleBlockquote().run()"
          >
            <EditorIcon name="quote" title="Block quote" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('undo')"
            type="button"
            class="editor-menu__button"
            :disabled="!editor.can().undo()"
            @click="editor.chain().focus().undo().run()"
          >
            <EditorIcon name="undo" title="Undo" />
          </button>

          <button
            v-if="isFeatureActive('redo')"
            type="button"
            class="editor-menu__button"
            :disabled="!editor.can().redo()"
            @click="editor.chain().focus().redo().run()"
          >
            <EditorIcon name="redo" title="Redo" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('code')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('code') }"
            @click="editor.chain().focus().toggleCode().run()"
          >
            <EditorIcon name="code" title="Code" />
          </button>

          <button
            v-if="isFeatureActive('codeBlock')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('codeBlock') }"
            @click="editor.chain().focus().toggleCodeBlock().run()"
          >
            <EditorIcon name="code_block" title="Code block" />
          </button>

          <button
            v-if="isFeatureActive('horizontalRule')"
            type="button"
            class="editor-menu__button"
            @click="editor.chain().focus().setHorizontalRule().run()"
          >
            <EditorIcon name="hr" title="Horizontal rule" />
          </button>

          <!-- Divider -->
          <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

          <button
            v-if="isFeatureActive('image')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('ImageResizable') }"
            @click="openImageModal"
          >
            <EditorIcon name="image" title="Add image" />
          </button>

          <button
            v-if="isFeatureActive('link')"
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('link') }"
            @click="openLinkModal"
          >
            <EditorIcon name="link" title="Add/edit link" />
          </button>

          <button
            v-if="showUnsetLink"
            type="button"
            class="editor-menu__button"
            :disabled="
              !editor.can().unsetLink() &&
              !editor.getAttributes('ImageResizable').link
            "
            @click="handleUnsetLink"
          >
            <EditorIcon name="remove_link" title="Remove link" />
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children"
            @click="toggleDropdown('table')"
          >
            <EditorIcon
              name="add_table"
              title="Add/remove table"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                @click="
                  editor
                    .chain()
                    .focus()
                    .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                    .run()
                "
              >
                <EditorIcon name="add_table" title="Add table" />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().deleteTable()"
                @click="editor.chain().focus().deleteTable().run()"
              >
                <EditorIcon name="delete_table" title="Delete table" />
              </button>
            </div>
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children"
            @click="toggleDropdown('table-row')"
          >
            <EditorIcon
              name="add_row_after"
              title="Add/remove row"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table-row'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().addRowAfter()"
                @click="editor.chain().focus().addRowAfter().run()"
              >
                <EditorIcon name="add_row_after" title="Add table row after" />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().deleteRow()"
                @click="editor.chain().focus().deleteRow().run()"
              >
                <EditorIcon name="delete_row" title="Delete table row after" />
              </button>
            </div>
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children"
            @click="toggleDropdown('table-col')"
          >
            <EditorIcon
              name="add_col_after"
              title="Add/remove column"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table-col'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().addColumnAfter()"
                @click="editor.chain().focus().addColumnAfter().run()"
              >
                <EditorIcon
                  name="add_col_after"
                  title="Add table column after"
                />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                :disabled="!editor.can().deleteColumn()"
                @click="editor.chain().focus().deleteColumn().run()"
              >
                <EditorIcon
                  name="delete_col"
                  title="Delete table column after"
                />
              </button>
            </div>
          </button>

          <button
            v-if="isFeatureActive('table')"
            type="button"
            class="editor-menu__button has-children is-highlighted"
            @click="toggleDropdown('table-format')"
          >
            <EditorIcon
              name="remove_border"
              title="Format table"
              :is-suggested="true"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'table-format'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                @click="() => unsetTable(false)"
              >
                <EditorIcon
                  name="add_border"
                  title="Show table borders"
                  :is-suggested="true"
                />
              </button>

              <button
                v-if="isFeatureActive('table')"
                type="button"
                class="editor-menu__button"
                @click="() => unsetTable(true)"
              >
                <EditorIcon
                  name="remove_border"
                  title="Hide table borders"
                  :is-suggested="true"
                />
              </button>
            </div>
          </button>

          <!-- Lists dropdown -->
          <button
            v-if="isFeatureActive('wand')"
            type="button"
            class="editor-menu__button has-children is-highlighted rounded-sm"
            :class="{
              'is-active':
                activeDropdown === 'wand' ||
                pasteOption === 'paste_without_formatting' ||
                pasteOption === 'paste_from_email',
            }"
            @click="toggleDropdown('wand')"
          >
            <EditorIcon
              name="wand"
              title="Special formatting"
              :is-suggested="true"
              :has-children="true"
            />
            <div
              v-if="activeDropdown === 'wand'"
              class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
            >
              <button
                v-if="isFeatureActive('paste_without_formatting')"
                type="button"
                class="editor-menu__button"
                :class="{
                  'is-active': pasteOption === 'paste_without_formatting',
                }"
                @click="setPasteOption('paste_without_formatting')"
              >
                <EditorIcon
                  name="paste_without_formatting"
                  title="Paste without formatting"
                  :is-suggested="true"
                />
              </button>

              <button
                v-if="isFeatureActive('paste_from_email')"
                type="button"
                class="editor-menu__button"
                :class="{ 'is-active': pasteOption === 'paste_from_email' }"
                @click="setPasteOption('paste_from_email')"
              >
                <EditorIcon
                  name="paste_from_email"
                  title="Paste from email"
                  :is-suggested="true"
                />
              </button>
            </div>
          </button>
        </div>

        <!--
          <button
          class="editor-menu__button"
          :class="{ 'is-active': editor.isActive('bulletList') }"
          @click="toggleDropdown('wand')"
          >
          <EditorIcon name="close" title="Close" />
          </button> 
        -->
      </div>
    </BubbleMenu>
    <div v-for="key in getMenuItems('BubbleMenu')" :key="key">
      <BubbleMenu
        v-if="editor"
        :editor="editor"
        :plugin-key="key"
        :should-show="(attr) => shouldShowMenu(attr, 'BubbleMenu', key)"
      >
        <div
          class="editor-menu items-center px-2 py-1 bg-white border border-truegray-300 shadow"
        >
          <div class="flex items-center">
            <!-- Headings dropdown -->
            <button
              v-if="isFeatureActive('headings')"
              type="button"
              class="editor-menu__button has-children"
              :class="{
                'is-active':
                  activeDropdown === 'headings' || editor.isActive('heading'),
              }"
              @click="toggleDropdown('headings')"
            >
              <EditorIcon
                name="headings"
                title="Headings"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'headings'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('h1')"
                  type="button"
                  class="editor-menu__button justify-center items-center"
                  :class="{
                    'is-active': editor.isActive('heading', { level: 1 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 1 }).run()
                  "
                >
                  <EditorIcon name="h1" title="Heading 1" />
                </button>

                <button
                  v-if="isFeatureActive('h2')"
                  type="button"
                  class="editor-menu__button justify-center items-center font-primary"
                  :class="{
                    'is-active': editor.isActive('heading', { level: 2 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 2 }).run()
                  "
                >
                  <EditorIcon name="h2" title="Heading 2" />
                </button>

                <button
                  v-if="isFeatureActive('h3')"
                  type="button"
                  class="editor-menu__button justify-center items-center font-primary"
                  :class="{
                    'is-active': editor.isActive('heading', { level: 3 }),
                  }"
                  @click="
                    editor.chain().focus().toggleHeading({ level: 3 }).run()
                  "
                >
                  <EditorIcon name="h3" title="Heading 3" />
                </button>
              </div>
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('bold')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('bold') }"
              @click="editor.chain().focus().toggleBold().run()"
            >
              <EditorIcon name="bold" title="Bold" />
            </button>
            <button
              v-if="isFeatureActive('italic')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('italic') }"
              @click="editor.chain().focus().toggleItalic().run()"
            >
              <EditorIcon name="italic" title="Italic" />
            </button>

            <button
              v-if="isFeatureActive('underline')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('underline') }"
              @click="editor.chain().focus().toggleUnderline().run()"
            >
              <EditorIcon name="underline" title="Underline" />
            </button>

            <button
              v-if="isFeatureActive('strike')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('strike') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <EditorIcon name="strike" title="Strikethrough" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('text_colour')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('text_colour') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <EditorIcon name="text_colour" title="Text colour" />
            </button>

            <button
              v-if="isFeatureActive('highlight')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('highlight') }"
              @click="editor.chain().focus().toggleStrike().run()"
            >
              <EditorIcon name="highlight" title="Highlight" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <!-- Lists dropdown -->
            <button
              v-if="isFeatureActive('lists')"
              type="button"
              class="editor-menu__button has-children"
              :class="{
                'is-active':
                  activeDropdown === 'lists' ||
                  editor.isActive('bulletList') ||
                  editor.isActive('orderedList'),
              }"
              @click="toggleDropdown('lists')"
            >
              <EditorIcon name="ol" title="Lists" :has-children="true" />
              <div
                v-if="activeDropdown === 'lists'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('bulletList')"
                  type="button"
                  class="editor-menu__button"
                  :class="{ 'is-active': editor.isActive('bulletList') }"
                  @click="editor.chain().focus().toggleBulletList().run()"
                >
                  <EditorIcon name="ul" title="Bullet list" />
                </button>

                <button
                  v-if="isFeatureActive('orderedList')"
                  type="button"
                  class="editor-menu__button"
                  :class="{ 'is-active': editor.isActive('orderedList') }"
                  @click="editor.chain().focus().toggleOrderedList().run()"
                >
                  <EditorIcon name="ol" title="Numbered list" />
                </button>
              </div>
            </button>

            <!-- Alignment dropdown -->
            <button
              v-if="isFeatureActive('alignments')"
              type="button"
              class="editor-menu__button has-children"
              :class="{
                'is-active':
                  activeDropdown === 'alignments' ||
                  editor.isActive({ textAlign: 'center' }) ||
                  editor.isActive({ textAlign: 'right' }),
              }"
              @click="toggleDropdown('alignments')"
            >
              <EditorIcon
                name="align_right"
                title="Text alignment"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'alignments'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('alignLeft')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': editor.isActive({ textAlign: 'left' }),
                  }"
                  @click="handleTextAlign('left')"
                >
                  <EditorIcon name="align_left" title="Align left" />
                </button>

                <button
                  v-if="isFeatureActive('alignCenter')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': editor.isActive({ textAlign: 'center' }),
                  }"
                  @click="handleTextAlign('center')"
                >
                  <EditorIcon name="align_center" title="Align center" />
                </button>

                <button
                  v-if="isFeatureActive('alignRight')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': editor.isActive({ textAlign: 'right' }),
                  }"
                  @click="handleTextAlign('right')"
                >
                  <EditorIcon name="align_right" title="Align right" />
                </button>
              </div>
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('blockquote')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('blockquote') }"
              @click="editor.chain().focus().toggleBlockquote().run()"
            >
              <EditorIcon name="quote" title="Block quote" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('undo')"
              type="button"
              class="editor-menu__button"
              :disabled="!editor.can().undo()"
              @click="editor.chain().focus().undo().run()"
            >
              <EditorIcon name="undo" title="Undo" />
            </button>

            <button
              v-if="isFeatureActive('redo')"
              type="button"
              class="editor-menu__button"
              :disabled="!editor.can().redo()"
              @click="editor.chain().focus().redo().run()"
            >
              <EditorIcon name="redo" title="Redo" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('code')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('code') }"
              @click="editor.chain().focus().toggleCode().run()"
            >
              <EditorIcon name="code" title="Code" />
            </button>

            <button
              v-if="isFeatureActive('codeBlock')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('codeBlock') }"
              @click="editor.chain().focus().toggleCodeBlock().run()"
            >
              <EditorIcon name="code_block" title="Code block" />
            </button>

            <button
              v-if="isFeatureActive('horizontalRule')"
              type="button"
              class="editor-menu__button"
              @click="editor.chain().focus().setHorizontalRule().run()"
            >
              <EditorIcon name="hr" title="Horizontal rule" />
            </button>

            <!-- Divider -->
            <!-- <div class="w-[1px] h-[1.2rem] mx-2 bg-truegray-300"></div>  -->

            <button
              v-if="isFeatureActive('image')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('ImageResizable') }"
              @click="openImageModal"
            >
              <EditorIcon name="image" title="Add image" />
            </button>

            <button
              v-if="isFeatureActive('link')"
              type="button"
              class="editor-menu__button"
              :class="{ 'is-active': editor.isActive('link') }"
              @click="openLinkModal"
            >
              <EditorIcon name="link" title="Add/edit link" />
            </button>

            <button
              v-if="showUnsetLink"
              type="button"
              class="editor-menu__button"
              :disabled="
                !editor.can().unsetLink() &&
                !editor.getAttributes('ImageResizable').link
              "
              @click="handleUnsetLink"
            >
              <EditorIcon name="remove_link" title="Remove link" />
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children"
              @click="toggleDropdown('table')"
            >
              <EditorIcon
                name="add_table"
                title="Add/remove table"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  @click="
                    editor
                      .chain()
                      .focus()
                      .insertTable({ rows: 3, cols: 3, withHeaderRow: true })
                      .run()
                  "
                >
                  <EditorIcon name="add_table" title="Add table" />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().deleteTable()"
                  @click="editor.chain().focus().deleteTable().run()"
                >
                  <EditorIcon name="delete_table" title="Delete table" />
                </button>
              </div>
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children"
              @click="toggleDropdown('table-row')"
            >
              <EditorIcon
                name="add_row_after"
                title="Add/remove row"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table-row'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().addRowAfter()"
                  @click="editor.chain().focus().addRowAfter().run()"
                >
                  <EditorIcon
                    name="add_row_after"
                    title="Add table row after"
                  />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().deleteRow()"
                  @click="editor.chain().focus().deleteRow().run()"
                >
                  <EditorIcon
                    name="delete_row"
                    title="Delete table row after"
                  />
                </button>
              </div>
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children"
              @click="toggleDropdown('table-col')"
            >
              <EditorIcon
                name="add_col_after"
                title="Add/remove column"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table-col'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().addColumnAfter()"
                  @click="editor.chain().focus().addColumnAfter().run()"
                >
                  <EditorIcon
                    name="add_col_after"
                    title="Add table column after"
                  />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  :disabled="!editor.can().deleteColumn()"
                  @click="editor.chain().focus().deleteColumn().run()"
                >
                  <EditorIcon
                    name="delete_col"
                    title="Delete table column after"
                  />
                </button>
              </div>
            </button>

            <button
              v-if="isFeatureActive('table')"
              type="button"
              class="editor-menu__button has-children is-highlighted"
              @click="toggleDropdown('table-format')"
            >
              <EditorIcon
                name="remove_border"
                title="Format table"
                :is-suggested="true"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'table-format'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  @click="() => unsetTable(false)"
                >
                  <EditorIcon
                    name="add_border"
                    title="Show table borders"
                    :is-suggested="true"
                  />
                </button>

                <button
                  v-if="isFeatureActive('table')"
                  type="button"
                  class="editor-menu__button"
                  @click="() => unsetTable(true)"
                >
                  <EditorIcon
                    name="remove_border"
                    title="Hide table borders"
                    :is-suggested="true"
                  />
                </button>
              </div>
            </button>

            <!-- Lists dropdown -->
            <button
              v-if="isFeatureActive('wand')"
              type="button"
              class="editor-menu__button has-children is-highlighted rounded-sm"
              :class="{
                'is-active':
                  activeDropdown === 'wand' ||
                  pasteOption === 'paste_without_formatting' ||
                  pasteOption === 'paste_from_email',
              }"
              @click="toggleDropdown('wand')"
            >
              <EditorIcon
                name="wand"
                title="Special formatting"
                :is-suggested="true"
                :has-children="true"
              />
              <div
                v-if="activeDropdown === 'wand'"
                class="top-[105%] left-[50%] transform -translate-x-1/2 rounded-sm bg-white border border-truegray-300 flex space-x-2 p-2 absolute z-40"
              >
                <button
                  v-if="isFeatureActive('paste_without_formatting')"
                  type="button"
                  class="editor-menu__button"
                  :class="{
                    'is-active': pasteOption === 'paste_without_formatting',
                  }"
                  @click="setPasteOption('paste_without_formatting')"
                >
                  <EditorIcon
                    name="paste_without_formatting"
                    title="Paste without formatting"
                    :is-suggested="true"
                  />
                </button>

                <button
                  v-if="isFeatureActive('paste_from_email')"
                  type="button"
                  class="editor-menu__button"
                  :class="{ 'is-active': pasteOption === 'paste_from_email' }"
                  @click="setPasteOption('paste_from_email')"
                >
                  <EditorIcon
                    name="paste_from_email"
                    title="Paste from email"
                    :is-suggested="true"
                  />
                </button>
              </div>
            </button>
          </div>

          <!--
            <button
            type="button"
            class="editor-menu__button"
            :class="{ 'is-active': editor.isActive('bulletList') }"
            @click="toggleDropdown('wand')"
            >
            <EditorIcon name="close" title="Close" />
            </button> 
          -->
        </div>
      </BubbleMenu>
    </div>
  </div>
</template>

<script>
import EditorIcon from './EditorIcon.vue'
import { FloatingMenu, BubbleMenu } from '@tiptap/vue-2'
import { isTextSelection } from '@tiptap/core'
import { EventBus } from '../../../../../js/plugins/event-bus.js'

export default {
  components: {
    EditorIcon,
    BubbleMenu,
    FloatingMenu,
  },

  props: {
    editor: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      activeDropdown: null,
      charLimit: 60,
      menuMap: {
        FloatingMenu: {
          newDoc: {
            useDefault: true,
            activeFeatures: [
              'headings',
              'h1',
              'h2',
              'h3',
              'wand',
              'paste_without_formatting',
              'paste_from_email',
            ],
          },

          newLine: {
            useDefault: true,
            activeFeatures: [
              'headings',
              'h1',
              'h2',
              'h3',
              'bold',
              'italic',
              'link',
              'image',
              'horizontalRule',
            ],
          },
        },

        BubbleMenu: {
          text: {
            useDefault: true,
            activeFeatures: {
              short: [
                'headings',
                'h1',
                'h2',
                'h3',
                'bold',
                'italic',
                'link',
                'alignments',
                'alignLeft',
                'alignCenter',
                'alignRight',
              ],

              long: [
                'bold',
                'italic',
                'blockquote',
                'alignments',
                'alignLeft',
                'alignCenter',
                'alignRight',
              ],
            },
          },

          lists: {
            useDefault: true,
            activeFeatures: {
              short: [
                'headings',
                'h1',
                'h2',
                'h3',
                'bold',
                'italic',
                'link',
                'lists',
                'bulletList',
                'orderedList',
              ],

              long: [
                'bold',
                'italic',
                'link',
                'lists',
                'bulletList',
                'orderedList',
              ],
            },
          },

          image: {
            useDefault: false,
            activeFeatures: ['image', 'link'],
          },

          link: {
            useDefault: true,
            activeFeatures: [
              'link',
              'bold',
              'italic',
              'alignments',
              'alignLeft',
              'alignCenter',
              'alignRight',
            ],
          },

          table: {
            useDefault: false,
            activeFeatures: {
              empty: ['table'],
              image: ['table', 'image'],
              text: [
                'table',
                'headings',
                'h1',
                'h2',
                'h3',
                'bold',
                'italic',
                'link',
                'alignments',
                'alignLeft',
                'alignCenter',
                'alignRight',
              ],
            },
          },
        },
      },
    }
  },

  computed: {
    view() {
      const { view } = this.editor || {}
      return view
    },

    state() {
      const { state } = this.editor || {}
      return state
    },

    selection() {
      return this.state?.selection
    },

    selectionRange() {
      const { from, to, empty } = this.selection || {}

      if (!this.selection || empty) {
        return 0
      }
      return to - from
    },

    selectionEmpty() {
      return !this.selectionRange
    },

    pasteOption() {
      return this.editor?.options?.editorProps?.attributes?.pasteOption
    },

    isNewLine() {
      // from Tiptap FloatingMenuPluginProps shouldShow source code
      const { $anchor, empty } = this.selection
      const isRootDepth = $anchor.depth === 1
      const isEmptyTextBlock =
        $anchor.parent.isTextblock &&
        !$anchor.parent.type.spec.code &&
        !$anchor.parent.textContent

      return empty && isRootDepth && isEmptyTextBlock
    },

    activeFeatures() {
      if (this.editor) {
        // new doc
        if (this.editor.isEmpty) {
          return this.menuMap?.FloatingMenu?.newDoc?.activeFeatures
        }

        // new line
        if (this.isNewLine) {
          return this.menuMap?.FloatingMenu?.newLine?.activeFeatures
        }

        // @todo check ONLY table is active - e.g., not all content selected
        if (this.editor.isActive('table')) {
          // if no child is selected
          if (!this.selectionRange) {
            return this.menuMap?.BubbleMenu?.table?.activeFeatures?.empty
          }

          // if child image is selected
          if (this.editor.isActive('ImageResizable')) {
            return this.menuMap?.BubbleMenu?.table?.activeFeatures?.image
          }

          // if child text element selected
          return this.menuMap?.BubbleMenu?.table?.activeFeatures?.text
        }

        // image
        if (this.editor.isActive('ImageResizable')) {
          return this.menuMap?.BubbleMenu?.image?.activeFeatures
        }

        // link
        if (this.editor.isActive('link')) {
          return this.menuMap?.BubbleMenu?.link?.activeFeatures
        }

        // list
        if (
          this.editor.isActive('orderedList') ||
          this.editor.isActive('bulletList')
        ) {
          if (this.selectionRange) {
            if (this.selectionRange <= this.charLimit) {
              return this.menuMap?.BubbleMenu?.lists?.activeFeatures?.short
            }

            return this.menuMap?.BubbleMenu?.lists?.activeFeatures?.long
          }
        }

        // text
        if (isTextSelection(this.selection)) {
          if (this.selectionRange) {
            if (this.selectionRange <= this.charLimit) {
              return this.menuMap.BubbleMenu?.text?.activeFeatures?.short
            }

            return this.menuMap.BubbleMenu?.text?.activeFeatures?.long
          }
        }
      }

      return null
    },

    showUnsetLink() {
      return (
        this.isFeatureActive('link') &&
        (this.editor.isActive('link') ||
          (this.editor.isActive('ImageResizable') &&
            !!this.editor.getAttributes('ImageResizable').link))
      )
    },
  },

  methods: {
    isFeatureActive(feature) {
      return this.activeFeatures?.includes(feature)
    },

    handleTextAlign(type) {
      this.$emit('handle-text-align', type)
    },

    openImageModal() {
      this.$emit('open-image-modal')
    },

    openLinkModal() {
      this.$emit('open-link-modal')
    },

    unsetTable(unset) {
      this.$emit('unset-table', unset)
    },

    handleUnsetLink() {
      this.$emit('handle-unset-link')
    },

    toggleDropdown(dropdown) {
      // this.editor.commands.focus()
      if (this.activeDropdown === dropdown) {
        this.activeDropdown = null
      } else {
        this.activeDropdown = dropdown
      }
    },

    setPasteOption(type) {
      this.$emit('set-paste-option', type)
    },

    getMenuItems(type) {
      const map = this.menuMap[type]
      return Object.keys(map).filter(
        (key) => !map[key].useDefault && !!map.activeFeatures,
      )
    },

    shouldShowMenu({ editor }, type, node) {
      // Floating menu
      if (type === 'FloatingMenu') {
        return editor.isActive(node) && this.selectionEmpty
      }

      // Bubble menu
      return editor.isActive(node)
    },

    showWandInfo() {
      EventBus.$emit('showModal', {
        action: 'editor-wand-info-modal',
      })
    },
  },
}
</script>
